/**
 * Base API client configuration using axios
 * Handles common request/response patterns and error handling
 */

import axios from 'axios'
import type { AxiosError, AxiosInstance } from 'axios'
import type { ApiResponse } from '@/types/common/api-response'
import { keycloak } from '@/lib/keycloak'
import { redirectToLogin } from '@/lib/auth-redirect'

// Base API URL - uses /api prefix for all requests (proxied via Vite in development)
// In production, VITE_API_URL should be set to the actual API URL (e.g., https://api.example.com/api)
const BASE_URL = import.meta.env.VITE_API_URL || '/api'

export type ApiError = {
  message: string
  code?: string
  status?: number
}

export class ApiClientError extends Error {
  code?: string
  status?: number
  /** Per-field validation reasons from the API (NestJS ValidationPipe details). */
  details?: Array<string>
  /**
   * The API's own reason code when it threw an object carrying one (e.g.
   * `{ code: 'ATTACHMENTS_TOO_LARGE' }`) — `code` stays the HTTP-level one
   * GlobalExceptionFilter assigns ('BAD_REQUEST', 'CONFLICT', …).
   */
  reasonCode?: string

  constructor(
    message: string,
    code?: string,
    status?: number,
    details?: Array<string>,
    reasonCode?: string,
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.code = code
    this.status = status
    this.details = details
    this.reasonCode = reasonCode
  }
}

/**
 * The error every failed API response becomes. The API wraps errors as
 * `{ success: false, error: { code, message, details } }`
 * (GlobalExceptionFilter); this reads that wire shape. Exported so specs can
 * run real response bodies through it.
 */
export function apiClientErrorFromResponse(
  status: number,
  statusText: string,
  data: unknown,
): ApiClientError {
  // API wraps errors as { success: false, error: { code, message, details } }
  const errorData = data as any
  const errorInfo = errorData?.error || {}
  const message =
    (typeof errorInfo === 'object' ? errorInfo.message : null) ||
    errorData?.message ||
    (typeof errorData?.error === 'string' ? errorData.error : null) ||
    `HTTP ${status}: ${statusText}`
  // Per-field reasons: global ValidationPipe puts them on `details`; the
  // wrapped { error: { details } } shape carries them under error.details.
  //
  // The third branch is the one that actually fires for a ValidationPipe
  // rejection. GlobalExceptionFilter assigns the WHOLE exception response
  // to error.details rather than response.details, so the array arrives one
  // level deeper as error.details.details. Unwrapped here rather than
  // flattened in the filter: the filter's shape is on the wire for every
  // API consumer, apps/portal included, and this is the only reason a
  // 400 ever reached a dialog as a bare "Validation failed".
  const nestedDetails = (errorInfo as { details?: { details?: unknown } })
    .details?.details
  const rawDetails: unknown = Array.isArray(errorData?.details)
    ? errorData.details
    : Array.isArray(errorInfo?.details)
      ? errorInfo.details
      : Array.isArray(nestedDetails)
        ? nestedDetails
        : undefined
  // A zod rejection (`new BadRequestException(result.error.issues)`)
  // arrives as issue objects, not strings — keep their `message`s.
  const details = Array.isArray(rawDetails)
    ? rawDetails
        .map((d: unknown) =>
          typeof d === 'string'
            ? d
            : d != null &&
                typeof (d as { message?: unknown }).message === 'string'
              ? (d as { message: string }).message
              : null,
        )
        .filter((d): d is string => d != null)
    : undefined
  const reasonCode =
    typeof errorInfo === 'object' &&
    typeof errorInfo?.details?.code === 'string'
      ? (errorInfo.details.code as string)
      : undefined
  return new ApiClientError(
    message,
    typeof errorInfo === 'object' ? errorInfo.code : errorData?.code,
    status,
    details,
    reasonCode,
  )
}

/**
 * Create axios instance with base configuration
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Request interceptor - Add authentication token to requests
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    if (keycloak.authenticated) {
      // Proactively refresh token if it expires within 30 seconds
      try {
        await keycloak.updateToken(30)
      } catch {
        // Token refresh failed — will get 401 and handle in response interceptor
      }
      if (keycloak.token) {
        config.headers.Authorization = `Bearer ${keycloak.token}`
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

/**
 * Response interceptor - Handle token refresh and extract data
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Skip unwrapping for non-JSON responses (e.g. PDF downloads, CSV preview text)
    if (
      response.config.responseType === 'blob' ||
      response.config.responseType === 'arraybuffer' ||
      response.config.responseType === 'text'
    ) {
      return response
    }

    // Check if response is in ApiResponse format
    if (
      response.data &&
      'success' in response.data &&
      'data' in response.data
    ) {
      const apiResponse = response.data as ApiResponse<unknown>

      // Check if the API response indicates an error (success: false)
      // Some backends return errors with 200 status but success: false
      if (apiResponse.success === false) {
        const errorInfo = (apiResponse as any).error || {}
        throw new ApiClientError(
          errorInfo.message || apiResponse.error || 'Request failed',
          errorInfo.code,
          response.status,
        )
      }

      // Return just the data payload, not the entire ApiResponse wrapper
      return apiResponse.data
    }
    return response.data
  },
  async (error: AxiosError) => {
    // A caller-aborted request (AbortSignal, e.g. the upload tray's cancel)
    // is not a failure: rethrow the axios CanceledError untouched so
    // `axios.isCancel(err)` still identifies it downstream instead of it
    // surfacing as a "No response from server" ApiClientError.
    if (axios.isCancel(error)) throw error

    const originalRequest = error.config

    // Handle 401 - Unauthorized (token expired or invalid)
    if (error.response?.status === 401) {
      let refreshed = false
      if (keycloak.authenticated) {
        try {
          refreshed = await keycloak.updateToken(5)
        } catch {
          refreshed = false
        }
      }

      if (refreshed && keycloak.token && originalRequest) {
        originalRequest.headers.Authorization = `Bearer ${keycloak.token}`
        return axiosInstance(originalRequest)
      }

      // Session is dead — can't refresh. Clear local token state and hard-redirect
      // to /login. We avoid kc.logout() here because Keycloak's logout endpoint is
      // unreliable when the server-side session no longer exists.
      try {
        keycloak.clearToken()
      } catch {
        // no-op: keycloak may not be initialized
      }
      redirectToLogin()
      throw new ApiClientError('Session expired', 'UNAUTHORIZED', 401)
    }

    // Handle other errors
    if (error.response) {
      // Server responded with error status
      throw apiClientErrorFromResponse(
        error.response.status,
        error.response.statusText,
        error.response.data,
      )
    } else if (error.request) {
      // Request was made but no response received
      throw new ApiClientError('No response from server', 'NETWORK_ERROR')
    } else {
      // Something else happened
      throw new ApiClientError(
        error.message || 'An unknown error occurred',
        'UNKNOWN_ERROR',
      )
    }
  },
)

/**
 * Export the configured axios instance and helper methods
 */
/**
 * Extracts the `filename="..."` value from a `Content-Disposition` response
 * header, returning `null` when the header is missing or unparsable. Shared
 * by any adapter that downloads a binary blob and wants the server-suggested
 * filename for the browser save dialog.
 */
export function parseContentDispositionFilename(
  contentDisposition: string | undefined,
): string | null {
  if (!contentDisposition) return null
  const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
  return match ? match[1] : null
}

export const apiClient = {
  // Direct access to axios instance for advanced usage
  instance: axiosInstance,

  // Helper methods for common HTTP operations
  // Note: These return the unwrapped data directly (handled by response interceptor)
  get: <T>(endpoint: string) => axiosInstance.get<ApiResponse<T>, T>(endpoint),

  /**
   * Fetch a file's bytes through the API.
   *
   * Used where a caller needs the FILE rather than a link — the PDF viewer
   * reads a blob, and `fetch()` against a signed bucket URL is a cross-origin
   * request the bucket's missing CORS policy blocks.
   */
  getBlob: async (endpoint: string): Promise<Blob> => {
    // The response interceptor deliberately does NOT unwrap a blob — there is
    // no ApiResponse envelope to unwrap — so it hands back the whole
    // AxiosResponse. The old signature asserted `<Blob, Blob>`, which told
    // TypeScript the resolved value WAS the blob and hid the mismatch: every
    // caller received a response object and passed it to URL.createObjectURL,
    // which failed with "Overload resolution failed". Unwrap it here, once,
    // rather than at each call site.
    const response = await axiosInstance.get<Blob>(endpoint, {
      responseType: 'blob',
    })
    return response.data
  },

  post: <T>(endpoint: string, data?: unknown) =>
    axiosInstance.post<ApiResponse<T>, T>(endpoint, data),

  /**
   * Multipart POST, for sending a file through the API.
   *
   * `Content-Type: undefined` is deliberate — it removes the instance-wide
   * JSON default so the browser can set `multipart/form-data` WITH the
   * boundary it generates. Leaving the JSON header on, or setting the
   * multipart type by hand without a boundary, makes the server unable to
   * parse the body.
   */
  postForm: <T>(
    endpoint: string,
    form: FormData,
    onProgress?: (percent: number) => void,
  ) =>
    axiosInstance.post<ApiResponse<T>, T>(endpoint, form, {
      headers: { 'Content-Type': undefined },
      onUploadProgress: onProgress
        ? (event) => {
            if (!event.total) return
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown) =>
    axiosInstance.put<ApiResponse<T>, T>(endpoint, data),

  patch: <T>(endpoint: string, data?: unknown) =>
    axiosInstance.patch<ApiResponse<T>, T>(endpoint, data),

  delete: <T>(endpoint: string) =>
    axiosInstance.delete<ApiResponse<T>, T>(endpoint),
}
